import { useContext } from 'react';
import Image from '@next/image';

import ClockIcon from 'assets/icons/clock.svg';
import MailIcon from 'assets/icons/mail-small.svg';
import { ShopConfigKey } from 'types/ShopConfig';
import { ShopConfig } from 'types/models/ShopConfig';
import { Shop } from 'types/models/Shop';
import { ConfigContext } from '@providers/ConfigProvider';
import { MenuItem } from '@templates/FooterMenu/FooterMenu.types';
import Link from '@components/atoms/Link';
import Typography from '@components/atoms/Typography';
import PhoneNumber from '@components/atoms/PhoneNumber';
import { CopyrightBox } from '@templates/CopyrightFooter';
import { FormattedMessage } from 'utils/intl';
import getListWithId from 'utils/getListWithId';

import MenuList from '../MenuList';
import FooterContactUs from '../FooterContactUs';
import CollapsibleMenuList from '../CollapsibleMenuList';
import classes from './MenuItemsSection.module.scss';
import messages from './MenuItemsSection.messages';

const getUrlByType = (config?: ShopConfig[], type?: ShopConfigKey): string | undefined =>
  config?.find(({ configKeyId }) => configKeyId === type)?.value;

type Props = {
  menuItems: MenuItem[];
  brands?: Shop[];
  withoutContact?: boolean;
  apiVersion?: string;
};

const Menu = ({ menuItems, brands = [], apiVersion, withoutContact }: Props): JSX.Element => {
  const { contact } = useContext(ConfigContext);

  const mobileMenuItems: (MenuItem & { isPhone?: boolean })[] = [
    ...menuItems.filter(({ name }) => name),
    {
      id: -1,
      name: <FormattedMessage {...messages.header} />,
      isPhone: true,
      items: getListWithId([
        {
          name: (
            <Typography variant="body2" weight="700" className={classes.row}>
              <ClockIcon className={classes.icon} />
              {contact.openingHours}
            </Typography>
          ),
          url: '',
          isNoFollow: false,
        },
        {
          name: <PhoneNumber tel={contact.landlinePhone} variant="landline" />,
          url: `tel:${contact.landlinePhone}`,
          isNoFollow: false,
        },
        ...(contact.cellPhone
          ? [
              {
                name: <PhoneNumber tel={contact.cellPhone} variant="mobile" />,
                url: `tel:${contact.cellPhone}`,
                isNoFollow: false,
              },
            ]
          : []),
        {
          name: (
            <Typography className={classes.row} variant="body2" weight="700">
              <MailIcon className={classes.icon} />
              {contact.contactEmail}
            </Typography>
          ),
          url: `mailto:${contact.contactEmail}`,
          isNoFollow: false,
        },
      ]),
    },
  ];

  return (
    <>
      <div className={classes.wrapper}>
        <div className={classes.menuItemsWrapper}>
          {menuItems.map(({ id, name, items }) => (
            <MenuList key={id} name={name} items={items} />
          ))}
        </div>
        <div className={classes.contactUs}>
          {!withoutContact && <FooterContactUs />}
          {!!brands.length && (
            <div className={classes.brands}>
              <Typography variant="h4" weight="700" renderAs="div" className={classes.brandsLabel}>
                <FormattedMessage {...messages.ourBrands} />
              </Typography>
              {brands.map(({ id, name, config, files }) => {
                const appUrl = getUrlByType(config, 'OVERRIDEN_FOOTER_URL') || getUrlByType(config, 'APP_URL') || '/';
                const logo = files?.find(({ name: fileName }) => fileName === 'logo');
                return (
                  <Link key={id} aria-label={`${name} logo`} href={appUrl}>
                    <Image
                      layout="fill"
                      objectFit="contain"
                      className={classes.shopLogo}
                      alt={`${logo?.name} ${name}`}
                      resolveConfig={{
                        preset: 'svg',
                        description: 'logo',
                        productName: name,
                        extension: logo?.file?.extension,
                        name: logo?.file?.basename,
                        width: logo?.file?.width,
                        height: logo?.file?.height,
                      }}
                      height={22}
                    />
                  </Link>
                );
              })}
            </div>
          )}
        </div>
        {!!apiVersion && <CopyrightBox apiVersion={apiVersion} />}
      </div>
      <div className={classes.mobileWrapper}>
        {mobileMenuItems.map(({ id, name, items, isPhone }) => (
          <CollapsibleMenuList key={id} name={name} isPhone={isPhone} items={items} />
        ))}
        {!!apiVersion && <CopyrightBox className={classes.mobileCopyright} apiVersion={apiVersion} />}
      </div>
    </>
  );
};

export default Menu;
